import React, { useEffect } from 'react';

import { Card, List } from '@mui/material';

import {
    GroupeTravailUtilisateurApiObject,
    useUtilisateurService,
} from '@europrocurement/l2d-domain';
import { EuroprocApiResponseStatus } from '@europrocurement/l2d-redux-utils';
import { JointureUtilisateurGroupeTravailJsonldUserworkgroupRead } from '@europrocurement/l2d-domain/openApi/ApiUtilisateur';
import { Box, useTheme } from '@mui/system';

import { CenterCircularProgress } from '@europrocurement/flexy-components';
import { MembersHeader } from './MembersHeader/MembersHeader';
import { MembersWidget } from './MembersWidgetContainer/MembersWidget';

export type WorkGroupMembersCardProps = {
    workgroup: GroupeTravailUtilisateurApiObject;
    isActiveStatus: boolean;
};

export const WorkGroupMembersCard: React.FunctionComponent<WorkGroupMembersCardProps> = function ({
    workgroup,
    isActiveStatus,
}) {
    // Apis.
    const { utilisateursGroupeTravailApi } = useUtilisateurService();

    // Mui.
    const theme = useTheme();

    // Members request status.
    const [status, setStatus] = React.useState<EuroprocApiResponseStatus>('idle');

    // Members request parameters.
    const [historicInscription, setHistoricInscription] = React.useState<boolean>(!isActiveStatus);

    // Members.
    const [members, setMembers] = React.useState<
        JointureUtilisateurGroupeTravailJsonldUserworkgroupRead[]
    >([]);

    // Check workgroup active status and set historicInscription default value accordingly only when false.
    useEffect(() => {
        if (isActiveStatus === false) {
            setHistoricInscription(true);
        }
    }, [isActiveStatus]);

    // Fetch Members.
    useEffect(() => {
        const getMembers = async () => {
            try {
                setStatus('loading');

                await utilisateursGroupeTravailApi
                    .apiUtilisateurgroupetravailGetCollection({
                        historicInscription,
                        groupetravail: `${workgroup.id}`,
                    })
                    .then((response) => {
                        setMembers(response.data['hydra:member']);
                    })
                    .then(() => setStatus('succeeded'));
            } catch (error) {
                setStatus('failed');
                console.error(error);
            }
        };
        getMembers();
    }, [historicInscription, workgroup, utilisateursGroupeTravailApi]);

    // Handle loading and error.
    if (status !== 'succeeded') {
        return (
            <Card
                sx={{
                    width: '49%',
                    margin: '10px 0',
                    padding: '0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                {status === 'failed' ? (
                    <Box>Error: no member found !</Box>
                ) : (
                    <CenterCircularProgress />
                )}
            </Card>
        );
    }

    return (
        <Card sx={{ width: '49%', margin: '10px 0', padding: '0' }}>
            <Box
                className="information-card"
                sx={{ backgroundColor: theme.palette.background.dark }}
            >
                <MembersHeader
                    workgroup={workgroup}
                    historicInscription={historicInscription}
                    setHistoricInscription={setHistoricInscription}
                    isActiveStatus={isActiveStatus}
                    members={members}
                />

                <List className="body">
                    {members.map((member) => (
                        <MembersWidget
                            key={member.id}
                            member={member}
                        />
                    ))}
                </List>
            </Box>
        </Card>
    );
};
